import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Modal from "@mui/material/Modal";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import HeaderOrg from "../Headers/HeaderOrg";
import ManageTitle from "../../Components/TitleElements/ManageTitle";
import Loader from "../../Components/Loader/Loader";
import Help from "../../Components/Help/Help";
import Checkbox from "../../Components/Checkbox/Checkbox";
import Button from "../../Components/Button/Button";
import { useJobDevice } from "../../device/DeviceContext";
import ConfirmDeleteModal from "../../Components/Modals/OrganizerModel/ManageFeis/Tabulate/ConfirmDeleteModal";
import editIcon from "../../Assets/edit-comp-action.svg";
import sortIcon from "../../Assets/managefeisGrade/sort-org.svg";
import closeIcon from "../../Assets/close-icon.svg";
import { getSortOrder } from "../../helper/CompTableSearch";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import { serviceConsumer } from "../../network/ServiceConsumer";
import {
  getEventCompetitors,
  downloadCompetitorsCsv,
  setStatusFlags,
  setEntryCompetitions,
  deleteEventEntry,
} from "../../network/oireachtasEventDay";

// Competitors — the dancer-centric roster (Devin's IA, 2026-08-17): every
// registered dancer once, with school and their FULL competition list (solo
// entries ∪ team memberships), searchable, sortable on every column (house
// sort-icon pattern per EditCompetitors), with the admin-set qualification
// flags marked HERE. Row actions: edit the dancer's SOLO competitions, or
// remove the dancer from the event — both pre-draw only (the server blocks
// once a touched comp's draw is locked; day-of withdrawals are check-in
// no-shows). Team membership is managed on the team, never from this screen.
const OireachtasCompetitors = () => {
  const [eventDoc, setEventDoc] = useState(null);
  const [rows, setRows] = useState([]);
  // an event-day device (card 540f21a4) only reads the list: flags, entry
  // edits and removals are never a device's
  const readOnly = !!useJobDevice().device;
  const [soloComps, setSoloComps] = useState([]); // event solo-comp catalog (edit modal)
  const [search, setSearch] = useState("");
  const [sorterData, setSorterData] = useState("dancer");
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [busyDancer, setBusyDancer] = useState("");
  const [editRow, setEditRow] = useState(null); // row being edited (modal open)
  const [editSet, setEditSet] = useState(new Set()); // draft solo comp ids
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null); // row pending delete confirm
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = new URLSearchParams(location.search).get("id");

  const load = async () => {
    const res = await getEventCompetitors(eventId);
    setRows(res.rows || []);
    setSoloComps(res.soloCompetitions || []);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [eventRes] = await Promise.all([
          serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`),
          load(),
        ]);
        setEventDoc(eventRes.event);
      } catch (e) {
        ShowToastMessage("error", e?.messages || "Could not load competitors");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // ---- flags ----
  const toggleFlag = async (row, key) => {
    try {
      setBusyDancer(row.dancer_id);
      await setStatusFlags(eventId, {
        dancer_id: row.dancer_id,
        worldMedalHolder: key === "worldMedalHolder" ? !row.worldMedalHolder : row.worldMedalHolder,
        // kept as stored: a deferred qualification changes nothing (only World
        // medal holders are skipped, CLRG ORnC r20; Devin 2026-10-29), so the
        // screen no longer sets it
        wqDeferred: row.wqDeferred,
      });
      await load();
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Only an admin may set status flags");
    } finally {
      setBusyDancer("");
    }
  };

  // ---- edit competitions ----
  const openEdit = (row) => {
    setEditRow(row);
    setEditSet(new Set(row.soloCompIds || []));
  };
  const toggleComp = (compId) => {
    const next = new Set(editSet);
    next.has(compId) ? next.delete(compId) : next.add(compId);
    setEditSet(next);
  };
  const saveEdit = async () => {
    try {
      setSaving(true);
      const res = await setEntryCompetitions(eventId, editRow.dancer_id, [...editSet]);
      ShowToastMessage("success", res.message || "Competitions updated");
      setEditRow(null);
      await load();
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Could not update competitions");
    } finally {
      setSaving(false);
    }
  };

  // ---- delete dancer ----
  const confirmDelete = async () => {
    const row = deleteRow;
    setDeleteRow(null);
    try {
      setBusyDancer(row.dancer_id);
      const res = await deleteEventEntry(eventId, row.dancer_id);
      ShowToastMessage("success", res.message || "Dancer removed");
      await load();
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Could not remove the dancer");
    } finally {
      setBusyDancer("");
    }
  };

  // ---- sort (house pattern: sort icon per header, asc/desc toggle) ----
  const SORTERS = {
    dancer: (r) => `${r.lname} ${r.fname}`.toLowerCase(),
    school: (r) => r.school.toLowerCase(),
    born: (r) => r.birthYear || 0,
    gender: (r) => r.gender.toLowerCase(),
    comps: (r) => [...r.soloComps, ...r.teamComps].join(" "),
    wmh: (r) => (r.worldMedalHolder ? 1 : 0),
  };
  const sortHandler = (sorter) => {
    const order = getSortOrder(sorter, sorterData, sortOrder);
    setSorterData(sorter);
    setSortOrder(order);
  };
  const sorted = [...rows].sort((a, b) => {
    const ka = SORTERS[sorterData](a);
    const kb = SORTERS[sorterData](b);
    const cmp = typeof ka === "number" ? ka - kb : String(ka).localeCompare(String(kb));
    return sortOrder === "asc" ? cmp : -cmp;
  });

  const q = search.trim().toLowerCase();
  const visible = q
    ? sorted.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.school.toLowerCase().includes(q) ||
          r.soloComps.concat(r.teamComps).some((c) => c.toLowerCase().includes(q))
      )
    : sorted;

  const th = (label, sorter, center) => (
    <th className={center ? "text-center" : undefined} style={{ whiteSpace: "nowrap" }}>
      {label}&nbsp;
      <img className="cursor-pointer" src={sortIcon} alt="" onClick={() => sortHandler(sorter)} />
    </th>
  );

  const eventDate = (eventDoc?.eventDays && eventDoc.eventDays[0]) || eventDoc?.entriesCloseDate;

  return (
    <>
      {loading && <Loader />}
      <HeaderOrg />
      <ManageTitle
        eventLogo={eventDoc?.logo}
        eventName={eventDoc?.name}
        eventType={eventDoc?.eventType}
        eventDate={eventDate}
        loading={loading}
        onBackClick={() => navigate(`/oireachtas/manage?id=${eventId}`)}
      />
      {!loading && (
        <div className="db-body pb-5">
          <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
            Every registered dancer with their full competition list (solo and team). World medal
            holders auto-qualify and are skipped in Worlds-qualifier allocation (the slot passes
            down); deferred and NAIDC qualifiers take a place like anyone else. Flags are admin-set. Edits and removals are pre-draw only —
            once a draw is locked, day-of withdrawals are check-in no-shows.
          </div>

          <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
            <input
              className="check-in-select"
              style={{ minWidth: "260px" }}
              placeholder="Search name, school or comp code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="grey-42" style={{ fontSize: "12px" }}>
              {visible.length} of {rows.length} dancers
            </span>
            <Button className="outlined" onClick={() => downloadCompetitorsCsv(eventId).catch(() => ShowToastMessage("error", "Export failed"))}>
              Export CSV
            </Button>
          </div>

          <div className="mt-4" style={{ overflowX: "auto" }}>
            <table className="table my_table competitorTable">
              <thead>
                <tr className="header-row">
                  {th("Dancer", "dancer")}
                  {th("School", "school")}
                  {th("Born", "born")}
                  {th("Gender", "gender")}
                  {th("Competitions", "comps")}
                  {th("World medal holder", "wmh", true)}
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.dancer_id} style={{ background: "#FFFFFF", fontSize: "12px" }}>
                    <td className="grey-30">{r.name}</td>
                    <td>{r.school}</td>
                    <td>{r.birthYear || "—"}</td>
                    <td>{r.gender}</td>
                    <td>
                      {r.soloComps.join(", ")}
                      {r.teamComps.length > 0 && (
                        <span className="grey-42">
                          {r.soloComps.length > 0 ? " · " : ""}
                          {r.teamComps.join(", ")}
                        </span>
                      )}
                    </td>
                    {["worldMedalHolder"].map((k) => (
                      <td key={k} className="text-center">
                        <Checkbox
                          checked={!!r[k]}
                          disabled={readOnly || busyDancer === r.dancer_id}
                          onChange={() => toggleFlag(r, k)}
                        />
                      </td>
                    ))}
                    <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                      {!readOnly && (
                        <>
                          <img
                            src={editIcon}
                            alt="edit"
                            style={{ cursor: "pointer" }}
                            onClick={() => openEdit(r)}
                          />
                          <DeleteOutlineOutlinedIcon
                            style={{ color: "red", marginLeft: "10px", cursor: "pointer" }}
                            onClick={() => setDeleteRow(r)}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visible.length === 0 && (
              <div className="d-flex justify-content-center my-5">
                {rows.length === 0 ? "No paid entries yet" : "No dancers match the search"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- edit competitions modal ---- */}
      <Modal open={!!editRow} onClose={() => !saving && setEditRow(null)}>
        <div
          style={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            background: "#FFFFFF", borderRadius: "8px", padding: "24px",
            width: "min(480px, 92vw)", maxHeight: "80vh", overflowY: "auto", outline: "none",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <b>{editRow?.name} — competitions</b>
            <img src={closeIcon} alt="close" style={{ cursor: "pointer" }} onClick={() => !saving && setEditRow(null)} />
          </div>
          <div className="grey-42 mb-3" style={{ fontSize: "12px" }}>
            Solo competitions only — team entries ({editRow?.teamComps?.join(", ") || "none"}) are
            managed on the team. Blocked once a changed competition's draw is locked.
          </div>
          {soloComps.map((c) => (
            <div key={c._id} className="d-flex align-items-center gap-2 my-1">
              <Checkbox checked={editSet.has(String(c._id))} onChange={() => toggleComp(String(c._id))} />
              <span style={{ fontSize: "13px" }}>
                {c.code} <span className="grey-42">— {c.name}</span>
              </span>
            </div>
          ))}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button className="text-btn" onClick={() => !saving && setEditRow(null)}>Cancel</Button>
            <Button className="filled" onClick={saveEdit} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ---- delete dancer confirm (house modal) ---- */}
      <ConfirmDeleteModal
        modalState={!!deleteRow}
        confirm={confirmDelete}
        cancel={() => setDeleteRow(null)}
        dancerPage={true}
      />
      <Help />
      <ToastContainer />
    </>
  );
};

export default OireachtasCompetitors;
